// Director reputation + philosophy model.
//
// Reputation has two parts:
//   STANDING  — a single prestige value: how big a Director you are, which
//               clubs will chase you. Climbs with success.
//   TRAITS    — who you are. Four independent 0-100 leans that clubs read to
//               decide if you fit what they need.
//
// The philosophy chosen at creation SEEDS this: it sets starting trait leans,
// start conditions, the board's objective, and which clubs court you. From
// there, in-game decisions evolve the traits and standing over time.

export const TRAITS = [
  { key: 'youth',     label: 'Youth',     colour: '#4DD8E8', blurb: 'Developing and promoting young talent' },
  { key: 'trader',    label: 'Trader',    colour: '#F5C65D', blurb: 'Buying low and selling high at a profit' },
  { key: 'winner',    label: 'Winner',    colour: '#C8F135', blurb: 'Chasing and delivering silverware now' },
  { key: 'stability', label: 'Stability', colour: '#A78BFA', blurb: 'Bringing calm, loyalty and a steady hand' },
];

export const START_STANDING = 30;

export const PHILOSOPHIES = [
  {
    key: 'academy_first',
    name: 'Academy First',
    tagline: 'Build and sell your own.',
    perk: 'You like to think of yourself as someone who develops young talent and sells it on for more.',
    tradeoff: 'Boards judge you on the talent you bring through, and a win-now club may not share your patience.',
    traits: { youth: 60, trader: 35, winner: 25, stability: 35 },
    effects: { budget: 'low', patience: 'standard' },
    objective: 'Blood academy players into the first team.',
    wants: ['Developing clubs', 'Strong academies', 'Tight budgets'],
  },
  {
    key: 'moneyball',
    name: 'Moneyball',
    tagline: 'Buy low, sell high.',
    perk: 'You like to think of yourself as someone who spots undervalued players and banks a profit.',
    tradeoff: 'Boards judge you on your dealing, and a club set on spending big may not value it.',
    traits: { youth: 40, trader: 60, winner: 30, stability: 25 },
    effects: { budget: 'standard', patience: 'standard' },
    objective: 'Finish transfer windows in profit.',
    wants: ['Cash-strapped clubs', 'Smart-spending boards'],
  },
  {
    key: 'win_now',
    name: 'Win Now',
    tagline: 'Silverware, immediately.',
    perk: 'You like to think of yourself as someone who wins, and wins now.',
    tradeoff: 'Boards expect trophies fast, and a club without the means to compete may leave you exposed.',
    traits: { youth: 20, trader: 30, winner: 60, stability: 25 },
    effects: { budget: 'high', patience: 'short' },
    objective: 'Win a trophy.',
    wants: ['Ambitious clubs', 'Wealthy owners'],
  },
  {
    key: 'the_fixer',
    name: 'The Fixer',
    tagline: 'People and stability first.',
    perk: 'You like to think of yourself as a steady hand who keeps a club calm and together.',
    tradeoff: 'Boards lean on you for stability, and a club chasing instant success may test your patience.',
    traits: { youth: 35, trader: 30, winner: 25, stability: 60 },
    effects: { budget: 'low', patience: 'high' },
    objective: 'Steady the club and keep the dressing room happy.',
    wants: ['Clubs in turmoil', 'Short-term crises'],
  },
];

export function philosophyByKey(key) {
  return PHILOSOPHIES.find((p) => p.key === key) || null;
}

// The reputation snapshot a Director starts with, given their philosophy.
// The game evolves this object over time; standing + the four traits.
export function startingReputation(key) {
  const p = philosophyByKey(key);
  if (!p) return null;
  return { standing: START_STANDING, ...p.traits };
}

// The trait a philosophy leans into hardest (for a quick label on the cards).
export function primaryTrait(key) {
  const p = philosophyByKey(key);
  if (!p) return null;
  const best = TRAITS.map((t) => ({ t, v: p.traits[t.key] || 0 }))
    .sort((a, b) => b.v - a.v)[0];
  return best ? best.t : null;
}
